import { Plus, SearchMd, Star01, XClose } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useImpersonateUser, useImpersonationTargets } from "@/api/admin/impersonate";
import { useScenarioRuns } from "@/api/admin/scenarios";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useDebounce } from "@/hooks/use-debounce";
import { cx } from "@/utils/cx";
import { CAN_CREATE_SCENARIOS, loadList, STORAGE_KEYS, SWITCHER_COPY, saveList } from "./impersonate/constants";
import { CreateScenario } from "./impersonate/create-scenario";
import { FilterMenu } from "./impersonate/filter-menu";
import {
  activeFilterCount,
  EMPTY_FILTERS,
  type Filters,
  filterRows,
  fullName,
  groupRows,
  type ListUser,
  MERCHANT_ROLES,
  merchantOptions,
  roleLabel,
  workspaceOptions,
} from "./impersonate/model";
import { UserRow } from "./impersonate/rows";
import { UserDetails } from "./impersonate/user-details";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Menu = "workspace" | "merchant" | "role" | null;

/**
 * Admin-only. Picks a user to browse the app as; the choice reloads into their session.
 * Favourites are kept in this browser.
 */
export const ImpersonateModal = ({ open, onOpenChange }: Props) => {
  const [view, setView] = useState<"list" | "create">("list");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [menu, setMenu] = useState<Menu>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [favourites, setFavourites] = useState<string[]>(() => loadList<string>(STORAGE_KEYS.favourites));

  const debouncedQuery = useDebounce(filters.query, 300);
  const { data: users = [], isLoading } = useImpersonationTargets(debouncedQuery, open);
  const { data: runs = [] } = useScenarioRuns(open && view === "create");
  const { mutate, isPending, error } = useImpersonateUser();

  const rows = useMemo(() => filterRows(users, filters), [users, filters]);
  const groups = useMemo(() => groupRows(rows, favourites), [rows, favourites]);
  const selected = rows.find((row) => row.user.id === selectedId) ?? null;

  const updateFavourites = (next: string[]) => {
    setFavourites(next);
    saveList(STORAGE_KEYS.favourites, next);
  };
  const toggleFavourite = (id: string) =>
    updateFavourites(favourites.includes(id) ? favourites.filter((f) => f !== id) : [...favourites, id]);

  const clearFilters = () => setFilters((current) => ({ ...current, workspaces: [], merchants: [], roles: [] }));

  const close = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedId(null);
    setMenu(null);
    setView("list");
    onOpenChange(false);
  };

  const impersonate = (user: ListUser) => mutate({ id: user.id });

  // Searching by the email brings the user into the list, which shows only the
  // first page of a search.
  const openScenario = (user: { id: string; email: string }) => {
    setFilters({ ...EMPTY_FILTERS, query: user.email });
    setSelectedId(user.id);
    setView("list");
  };

  useEffect(() => {
    if (!open || view !== "list") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (menu || (event.target as HTMLElement | null)?.closest("[data-row]")) return;
      if (event.key === "Enter") {
        if (selected) {
          event.preventDefault();
          impersonate(selected.user);
        }
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      if (rows.length === 0) return;
      const index = rows.findIndex((row) => row.user.id === selectedId);
      const next =
        index === -1 ? 0 : event.key === "ArrowDown" ? Math.min(index + 1, rows.length - 1) : Math.max(index - 1, 0);
      setSelectedId(rows[next]?.user.id ?? null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent hideCloseButton className="max-w-[1020px] gap-0 overflow-hidden p-0">
        {view === "create" ? (
          <CreateScenario runs={runs} onBack={() => setView("list")} onClose={close} onOpen={openScenario} />
        ) : (
          <div className="flex max-h-[85vh] min-w-0 flex-col gap-4 bg-unbox-cream-25 p-6">
            <div className="flex items-start justify-between gap-6">
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-lg font-semibold text-gray-900">{SWITCHER_COPY.title}</DialogTitle>
                <DialogDescription className="text-sm text-gray-600">{SWITCHER_COPY.description}</DialogDescription>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {CAN_CREATE_SCENARIOS && (
                  <Button
                    color="primary"
                    size="sm"
                    iconLeading={Plus}
                    onClick={() => {
                      setMenu(null);
                      setView("create");
                    }}
                  >
                    Create new scenario
                  </Button>
                )}
                <button
                  type="button"
                  aria-label="Close"
                  onClick={close}
                  className="-mt-1 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-900/5 hover:text-gray-700"
                >
                  <XClose className="size-5" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  aria-label="Search users"
                  // biome-ignore lint/a11y/noAutofocus: search is the first thing an admin does in this modal
                  autoFocus
                  icon={SearchMd}
                  placeholder="Search name, email, workspace or merchant"
                  value={filters.query}
                  onChange={(query) => setFilters((current) => ({ ...current, query }))}
                />
              </div>
              <FilterMenu
                label="Workspace"
                options={workspaceOptions(users)}
                selected={filters.workspaces}
                onChange={(workspaces) => setFilters((current) => ({ ...current, workspaces }))}
                searchable
                open={menu === "workspace"}
                onOpen={() => setMenu("workspace")}
                onClose={() => setMenu(null)}
              />
              <FilterMenu
                label="Merchant"
                options={merchantOptions(users)}
                selected={filters.merchants}
                onChange={(merchants) => setFilters((current) => ({ ...current, merchants }))}
                searchable
                open={menu === "merchant"}
                onOpen={() => setMenu("merchant")}
                onClose={() => setMenu(null)}
              />
              <FilterMenu
                label="Role"
                options={MERCHANT_ROLES.map((role) => ({ value: role, label: roleLabel(role), count: 0 }))}
                selected={filters.roles}
                onChange={(roles) => setFilters((current) => ({ ...current, roles: roles as Filters["roles"] }))}
                align="end"
                open={menu === "role"}
                onOpen={() => setMenu("role")}
                onClose={() => setMenu(null)}
              />
              {activeFilterCount(filters) > 0 && (
                <Button color="link-color" size="sm" onClick={clearFilters}>
                  Clear all
                </Button>
              )}
              <span className="ml-auto text-sm text-gray-500">
                {rows.length} {rows.length === 1 ? "user" : "users"}
              </span>
            </div>

            {error && <p className="text-sm text-error-primary">Could not start that session. Try again.</p>}

            <div className="flex min-h-[420px] min-w-0 flex-1 gap-4 overflow-hidden">
              <div
                className={cx("flex min-h-0 flex-col gap-7 overflow-y-auto", selected ? "min-w-0 flex-1" : "w-full")}
              >
                {groups.length === 0 ? (
                  <p className="rounded-xl border border-gray-200 bg-white px-3 py-8 text-center text-sm text-gray-500">
                    {isLoading ? "Loading…" : "No users match these filters."}
                  </p>
                ) : (
                  groups.map((group, index) => (
                    <div
                      key={group.key}
                      className={cx("flex shrink-0 flex-col gap-2", index > 0 && "border-t border-gray-200 pt-7")}
                    >
                      {group.label && (
                        <div className="flex flex-col gap-0.5 px-1">
                          <p className="flex min-w-0 items-center gap-3 text-md font-semibold text-purple-950">
                            <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                              {group.star && (
                                <Star01 className="size-4 fill-current text-warning-600" aria-hidden="true" />
                              )}
                              {group.label}
                            </span>
                          </p>
                        </div>
                      )}
                      <div className="flex flex-col divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                        {group.rows.map((row) => (
                          <UserRow
                            key={row.user.id}
                            user={row.user}
                            workspaces={row.workspaces}
                            query={filters.query}
                            compact={!!selected}
                            selected={row.user.id === selectedId}
                            onSelect={() => setSelectedId((current) => (current === row.user.id ? null : row.user.id))}
                            favourite={favourites.includes(row.user.id)}
                            onToggleFavourite={() => toggleFavourite(row.user.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {selected && (
                <div className="flex w-[400px] min-w-0 shrink flex-col gap-2">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-md font-semibold text-purple-950">Selected user</p>
                    <button
                      type="button"
                      aria-label="Close details"
                      onClick={() => setSelectedId(null)}
                      className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-900/5 hover:text-gray-700"
                    >
                      <XClose className="size-4" />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto rounded-xl bg-unbox-cream-50">
                    <UserDetails user={selected.user} workspaces={selected.workspaces} />
                  </div>
                </div>
              )}
            </div>

            {selected && (
              <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-5">
                <Button color="secondary" size="md" onClick={close}>
                  Cancel
                </Button>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <Button
                    color="primary"
                    size="md"
                    disabled={isPending}
                    onClick={() => selected && impersonate(selected.user)}
                  >
                    {SWITCHER_COPY.start(fullName(selected.user).split(" ")[0])}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
